import { createPortal } from "react-dom";
import { useWhoDrugWorkspace } from "../../shared/context/AgentCapabilityContext";
import { useRef } from "react";
import { X } from "lucide-react";
import { useAgentThread } from "../../shared/context/AgentThreadContext";
import { DOCK_LABELS, RUN_ERROR_LABELS } from "../../shared/constants/pageAgentLabels";
import { useAgentConversation } from "../hooks/useAgentConversation";
import { useAgentDock } from "../hooks/useAgentDock";
import { useFieldActionComposer } from "../hooks/useFieldActionComposer";
import { useClearChatConfirm } from "../hooks/useClearChatConfirm";
import { ClearChatButton } from "./ClearChatButton";
import { ClearChatConfirm } from "./ClearChatConfirm";
import { AgentComposer } from "./AgentComposer";
import { ProposalCard } from "./ProposalCard";
import { AgentLauncher } from "./AgentLauncher";
import { AgentRunErrorBanner } from "./AgentRunErrorBanner";
import { AgentTranscript } from "./AgentTranscript";
import { AgentWorkingIndicator } from "./AgentWorkingIndicator";
import { useFollowLatest } from "../hooks/useFollowLatest";

/**
 * Theragenie on every authenticated full-layout screen: the launcher, and the panel that holds the
 * conversation. Mounted by `AuthFullLayout`, so routing decides where it appears.
 */
export function AgentDock() {
  const { available } = useAgentThread();
  const { host } = useWhoDrugWorkspace();
  const dock = useAgentDock(host);
  const conversation = useAgentConversation();
  useFieldActionComposer(conversation, dock.openDock, available);
  const clearChat = useClearChatConfirm(conversation.clearConversation, dock.composerRef);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useFollowLatest(bodyRef, conversation.contentVersion, conversation.busy && conversation.toolCalls.length === 0);
  const panel = dock.open && available ? (
        <section ref={dock.panelRef} className="pa-panel" role="dialog" aria-modal="false"
          aria-labelledby={dock.titleId} aria-describedby={dock.descriptionId}
          aria-busy={conversation.busy} onKeyDown={dock.onPanelKeyDown}>
          <header className="pa-panel-header">
            <div>
              <h2 id={dock.titleId} className="pa-panel-title">{DOCK_LABELS.title}</h2>
              <p id={dock.descriptionId} className="pa-panel-description">{DOCK_LABELS.description}</p>
            </div>
            <div className="pa-header-actions">
              {!conversation.loading && !conversation.unavailable && <ClearChatButton controls={clearChat} enabled={conversation.canClear} />}
              {host === null && <button type="button" className="pa-icon-button" onClick={dock.closeDock}>
                <X aria-hidden="true" className="pa-icon" />
                <span className="pa-visually-hidden">{DOCK_LABELS.close}</span>
              </button>}
            </div>
          </header>
          {!conversation.loading && !conversation.unavailable && (
            <ClearChatConfirm controls={clearChat} clearing={conversation.clearing} enabled={conversation.canClear} />
          )}
          <div className="pa-panel-body" ref={bodyRef}>
            {conversation.loading ? (
              <p className="pa-muted" role="status">{DOCK_LABELS.loading}</p>
            ) : conversation.unavailable ? (
              <p className="pa-muted">{RUN_ERROR_LABELS.unavailable}</p>
            ) : (
              <>
                {conversation.threadError !== null && <p className="pa-error" role="alert">{conversation.threadError}</p>}
                <AgentTranscript turns={conversation.turns} streaming={conversation.busy}
                  activity={conversation.workingLabel !== null && <AgentWorkingIndicator label={conversation.workingLabel} />}
                  outcome={conversation.proposals.length > 0 && (
                    <ProposalCard rows={conversation.proposals} decisions={conversation.proposalDecisions} versions={conversation.proposalVersions}
                      closed={conversation.closedProposalKeys}
                      onAccept={conversation.acceptProposal} onReject={conversation.rejectProposal} onUndo={conversation.undoProposal}
                      onRedo={conversation.redoProposal} onGoToField={conversation.goToProposal} />
                  )} />
                <AgentRunErrorBanner message={conversation.runError} />
                <AgentRunErrorBanner message={conversation.clearError} />
              </>
            )}
          </div>
          {!conversation.loading && !conversation.unavailable && (
            <AgentComposer question={conversation.question} onChange={conversation.setQuestion} chip={conversation.fieldActionChip}
              onClearChip={conversation.clearFieldAction} inputRef={dock.composerRef}
              onSend={() => void conversation.ask()} onStop={() => void conversation.stop()} busy={conversation.busy} />
          )}
        </section>
      ) : null;
  return <>
    {host === null && <AgentLauncher ref={dock.launcherRef} available={available} open={dock.open} onOpen={dock.openDock} />}
    {host ? createPortal(panel, host) : panel}
  </>;
}

